import { useEffect, useState } from "react";

import { useProfile, useUpdateProfile } from "../api";
import { readApiErrorMessage } from "../errors";

// Phase 4a customer-portal FR-9 (Contact tab). Email shown as a
// verified badge but read-only (email-change flow is out of v1).
// Comms preferences live on `preferred_notification_channel`.
export function ContactTab(): JSX.Element {
  const q = useProfile();
  const save = useUpdateProfile();

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!q.data || dirty) return;
    setFirst(q.data.first_name ?? "");
    setLast(q.data.last_name ?? "");
    setPhone(q.data.phone ?? "");
    setCompany(q.data.company_name ?? "");
    setChannel(q.data.preferred_notification_channel === "sms" ? "sms" : "email");
  }, [q.data, dirty]);

  function mark<T>(setter: (v: T) => void) {
    return (v: T) => {
      setDirty(true);
      setter(v);
    };
  }

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-red-700">Couldn&rsquo;t load profile.</div>;

  function handleSave(): void {
    setSaveError(null);
    save.mutate(
      {
        first_name: first,
        last_name: last,
        phone,
        company_name: company,
        preferred_notification_channel: channel
      },
      {
        onSuccess: () => setDirty(false),
        onError: (e) => setSaveError(readApiErrorMessage(e))
      }
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <Field label="Email">
        <span className="font-mono">{q.data.email}</span>{" "}
        {q.data.email_confirmed ? (
          <span className="text-xs text-green-700">verified</span>
        ) : (
          <span className="text-xs text-amber-700">unverified</span>
        )}
      </Field>
      <Field label="First name">
        <Input value={first} onChange={mark(setFirst)} testid="profile-first" />
      </Field>
      <Field label="Last name">
        <Input value={last} onChange={mark(setLast)} testid="profile-last" />
      </Field>
      <Field label="Phone">
        <Input value={phone} onChange={mark(setPhone)} testid="profile-phone" />
      </Field>
      <Field label="Company (optional)">
        <Input value={company} onChange={mark(setCompany)} testid="profile-company" />
      </Field>
      <Field label="Notify me by">
        <select
          value={channel}
          onChange={(e) => mark(setChannel)(e.target.value as "email" | "sms")}
          className="min-h-[44px] rounded border px-3 py-2"
          data-testid="profile-channel"
        >
          <option value="email">Email</option>
          <option value="sms">SMS (coming soon)</option>
        </select>
      </Field>
      <button
        type="button"
        onClick={handleSave}
        disabled={save.isPending}
        className="rounded bg-zinc-900 px-4 py-2 text-sm text-white disabled:opacity-40"
        data-testid="profile-save"
      >
        {save.isPending ? "Saving…" : "Save"}
      </button>
      {save.isSuccess && !dirty ? <span className="ml-2 text-xs text-green-700">Saved</span> : null}
      {saveError ? (
        <div className="mt-2 rounded border border-red-600 bg-red-50 p-2 text-sm" data-testid="profile-save-error">
          {saveError}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="block text-xs text-zinc-500">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  testid
}: {
  value: string;
  onChange: (v: string) => void;
  testid: string;
}): JSX.Element {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[44px] w-full rounded border px-3 py-2"
      data-testid={testid}
    />
  );
}
